/**
 * RecurrenceSection.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Seção de recorrência do AppointmentDrawer (coluna esquerda).
 *
 * Responsável pela UI de configuração da recorrência:
 *  - seletor de modo (agendamento único / recorrente)
 *  - configuração (frequência de repetição + quantidade)
 *  - botão de recalcular datas (ação secundária)
 *  - mensagem informativa sobre o que será criado
 *
 * A lista de ocorrências e o resumo foram movidos para RecurrenceSummaryPanel
 * (coluna direita do drawer no modo recorrente).
 *
 * Toda a orquestração (chamadas de API, criação, envio da confirmação) fica no
 * AppointmentDrawer. Este componente é apresentacional e controlado por props.
 *
 * @module RecurrenceSection
 */

import React from 'react';
import {
  INTERVAL_OPTIONS,
  MIN_OCCURRENCES,
  MAX_OCCURRENCES,
} from '../../utils/recurrenceUtils';
import styles from './RecurrenceSection.module.css';
import drawerStyles from './AppointmentDrawer.module.css';

/** Modos de criação suportados pelo drawer */
export const APPOINTMENT_MODE = {
  SINGLE: 'single',
  RECURRING: 'recurring',
};

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const RepeatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

/**
 * Seção de configuração da recorrência (coluna esquerda do drawer).
 *
 * @param {Object} props
 * @param {string} props.mode - APPOINTMENT_MODE.SINGLE | APPOINTMENT_MODE.RECURRING
 * @param {Function} props.onModeChange - (mode) => void
 * @param {number} props.intervalDays
 * @param {Function} props.onIntervalDaysChange
 * @param {number} props.occurrencesCount
 * @param {Function} props.onOccurrencesCountChange
 * @param {Array|null} props.occurrences - Ocorrências revisadas (null = ainda não calculadas)
 * @param {Function} props.onCheckAvailability - Dispara o cálculo/preview
 * @param {boolean} props.isChecking
 * @param {string} [props.error]
 * @param {Object} props.slot - { date: Date, hour: number, minute: number }
 * @returns {React.ReactElement}
 */
export default function RecurrenceSection({
  mode,
  onModeChange,
  intervalDays,
  onIntervalDaysChange,
  occurrencesCount,
  onOccurrencesCountChange,
  occurrences,
  onCheckAvailability,
  isChecking = false,
  error,
  slot,
}) {
  const isRecurring = mode === APPOINTMENT_MODE.RECURRING;

  // Formata a mensagem informativa sobre o que será criado
  const infoMessage = React.useMemo(() => {
    if (!slot) return null;
    const intervalOpt = INTERVAL_OPTIONS.find(o => o.days === intervalDays);
    const freqLabel = intervalOpt ? intervalOpt.label.toLowerCase() : `a cada ${intervalDays} dias`;
    const h = String(slot.hour).padStart(2, '0');
    const m = String(slot.minute).padStart(2, '0');
    const weekdays = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
    const weekday = weekdays[slot.date?.getDay() ?? 0];
    return `Serão criados até ${occurrencesCount} agendamentos a partir de ${weekday}, ${slot.date?.toLocaleDateString('pt-BR')} às ${h}:${m}`;
  }, [slot, intervalDays, occurrencesCount]);

  return (
    <>
      {/* ── Seletor de modo ──────────────────────────────────────────────── */}
      <div className={drawerStyles.formSection}>
        <label className={drawerStyles.fieldLabel}>Tipo de agendamento</label>
        <div className={styles.modeToggle}>
          <button
            type="button"
            className={`${styles.modeButton} ${!isRecurring ? styles.modeButtonActive : ''}`}
            onClick={() => onModeChange(APPOINTMENT_MODE.SINGLE)}
          >
            <CalendarIcon />
            Único
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${isRecurring ? styles.modeButtonActive : ''}`}
            onClick={() => onModeChange(APPOINTMENT_MODE.RECURRING)}
          >
            <RepeatIcon />
            Recorrente
          </button>
        </div>
      </div>

      {isRecurring && (
        <>
          {/* ── Título da seção de recorrência ─────────────────────────── */}
          <div className={styles.recurrenceSectionHeader}>
            <span className={styles.recurrenceSectionTitle}>Recorrência</span>
            <span className={styles.recurrenceSectionSubtitle}>Defina como os agendamentos serão repetidos</span>
          </div>

          {/* ── Configuração da recorrência ────────────────────────────── */}
          <div className={drawerStyles.formGrid2}>
            <div className={drawerStyles.formSection}>
              <label className={drawerStyles.fieldLabel}>Repetir</label>
              <select
                className={drawerStyles.fieldSelect}
                value={intervalDays}
                onChange={(e) => onIntervalDaysChange(Number(e.target.value))}
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt.days} value={opt.days}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={drawerStyles.formSection}>
              <label className={drawerStyles.fieldLabel}>Quantidade</label>
              <select
                className={drawerStyles.fieldSelect}
                value={occurrencesCount}
                onChange={(e) => onOccurrencesCountChange(Number(e.target.value))}
              >
                {Array.from(
                  { length: MAX_OCCURRENCES - MIN_OCCURRENCES + 1 },
                  (_, i) => MIN_OCCURRENCES + i
                ).map((count) => (
                  <option key={count} value={count}>
                    {count} agendamentos
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Mensagem informativa ───────────────────────────────────── */}
          {infoMessage && (
            <div className={styles.infoMessage}>
              <span className={styles.infoMessageIcon}><InfoIcon /></span>
              <span className={styles.infoMessageText}>{infoMessage}</span>
            </div>
          )}

          {/* ── Botão de recalcular (ação secundária) ─────────────────── */}
          <button
            type="button"
            className={styles.checkButton}
            onClick={onCheckAvailability}
            disabled={isChecking}
          >
            <RepeatIcon />
            {isChecking
              ? 'Verificando disponibilidade...'
              : occurrences
                ? 'Recalcular datas'
                : 'Verificar disponibilidade'}
          </button>

          {error && (
            <span className={drawerStyles.validationError}>{error}</span>
          )}
        </>
      )}
    </>
  );
}
